/**
 * Rotating "spinner words" shown while the agent works (TD-2334 / TD-2341).
 *
 * Claude-Code-style: a whimsical-but-on-brand status word that swaps every few seconds so a
 * long wait never looks frozen. Tasteful for a corporate-finance / M&A audience. The chat is
 * bilingual; pick the set by the UI locale (see detectUiLocale / getStatusWords).
 *
 * TD-2341: words are grouped into FAMILIES so each kind of work reads honestly —
 *   - characteristics : a company-characteristics search (what a company does: industry, value
 *                       chain, products, financials, shareholders, HQ/geo, special situations)
 *   - comparables     : a similar-company search (peers by value creation, financials, model)
 *   - generic         : any other tool the agent runs
 * Honest, specific tool labels are shown separately in the grey box (see toolDisplayConfig).
 */

const STATUS_WORDS = {
  characteristics: {
    en: [
      'Filtering by industry…',
      'Scanning value chains…',
      'Mapping product portfolios…',
      'Crunching financials…',
      'Sifting shareholders…',
      'Checking HQ & geography…',
      'Screening special situations…',
      'Profiling targets…',
      'Matching criteria…',
      'Vetting candidates…',
    ],
    de: [
      'Filtere nach Branche…',
      'Prüfe Wertschöpfungsketten…',
      'Kartiere Produktportfolios…',
      'Analysiere Finanzen…',
      'Prüfe Gesellschafter…',
      'Prüfe Sitz & Region…',
      'Screene Sondersituationen…',
      'Erstelle Profile…',
      'Gleiche Kriterien ab…',
      'Prüfe Kandidaten…',
    ],
  },
  comparables: {
    en: [
      'Finding look-alikes…',
      'Comparing value creation…',
      'Benchmarking financials…',
      'Matching business models…',
      'Sizing up peers…',
      'Scoring similarity…',
      'Ranking comparables…',
      'Aligning industries…',
      'Shortlisting peers…',
      'Vetting matches…',
    ],
    de: [
      'Suche Vergleichbare…',
      'Vergleiche Wertschöpfung…',
      'Vergleiche Kennzahlen…',
      'Gleiche Geschäftsmodelle ab…',
      'Bewerte Peers…',
      'Berechne Ähnlichkeit…',
      'Sortiere Vergleichswerte…',
      'Ordne Branchen zu…',
      'Erstelle Peer-Auswahl…',
      'Prüfe Treffer…',
    ],
  },
  generic: {
    en: [
      'Working…',
      'Fetching…',
      'Looking it up…',
      'Gathering data…',
      'Crunching…',
      'Pulling details…',
      'Compiling…',
      'On it…',
      'Retrieving…',
      'Digging in…',
    ],
    de: [
      'Arbeite…',
      'Hole Daten…',
      'Schlage nach…',
      'Sammle Daten…',
      'Verarbeite…',
      'Lade Details…',
      'Stelle zusammen…',
      'Bin dran…',
      'Rufe ab…',
      'Grabe tiefer…',
    ],
  },
};

const DEFAULT_FAMILY = 'generic';

/**
 * Map a raw search-tool kind to a word family. The kind is the bare token from
 * [SEARCH_STARTED:<kind>:id] (e.g. 'characteristics_search' | 'comparables_search'); any other
 * tool falls back to the generic family.
 * @param {string} [searchType]
 * @returns {'characteristics' | 'comparables' | 'generic'}
 */
export function familyFromSearchType(searchType) {
  if (searchType?.startsWith('characteristics')) return 'characteristics';
  if (searchType?.startsWith('comparables')) return 'comparables';
  return DEFAULT_FAMILY;
}

/**
 * UI locale derived from the /{lang}/ path segment (no FE i18n framework today).
 * Uses globalThis.location so it is safe under SSR / non-browser runtimes. Defaults to 'en'.
 * @returns {'en' | 'de'}
 */
export function detectUiLocale() {
  const path = globalThis.location?.pathname ?? '';
  return path.split('/')[1] === 'de' ? 'de' : 'en';
}

/**
 * Word list for a UI locale + family. Falls back to the generic family for an unknown family
 * and to English for an unknown locale.
 * @param {string} locale - 'en' | 'de'
 * @param {string} [family='generic'] - 'characteristics' | 'comparables' | 'generic'
 * @returns {string[]}
 */
export function getStatusWords(locale, family = DEFAULT_FAMILY) {
  const set = STATUS_WORDS[family] || STATUS_WORDS[DEFAULT_FAMILY];
  return set[locale] || set.en;
}
